import React from 'react'
import {Link} from 'react-router-dom'
import {motion} from 'framer-motion'
import {useSiteContent} from '../../hooks/useSiteContent'
import SafeIcon from '../../common/SafeIcon'
import * as FiIcons from 'react-icons/fi'

const {FiArrowRight, FiCalendar, FiTrendingUp} = FiIcons

const HeroSection = ({onCTAClick}) => {
  const {content} = useSiteContent()

  const handlePrimaryCTA = () => {
    onCTAClick?.('explore_courses', content?.cta_primary_url || '/courses')
  }

  const handleSecondaryCTA = () => {
    onCTAClick?.('book_call', content?.cta_secondary_url || 'https://tidycal.com/empowernutrition/30-minute-consultation')
  }

  return (
    <section className="relative bg-gradient-to-br from-nutrition-50 to-white py-20 lg:py-32 overflow-hidden">
      {/* Background Pattern */}
      <div className="absolute inset-0 opacity-5">
        <div className="absolute top-10 left-10 w-20 h-20 bg-nutrition-500 rounded-full"></div>
        <div className="absolute top-32 right-20 w-16 h-16 bg-nutrition-600 rounded-full"></div>
        <div className="absolute bottom-20 left-1/4 w-12 h-12 bg-nutrition-400 rounded-full"></div>
        <div className="absolute bottom-40 right-10 w-24 h-24 bg-nutrition-300 rounded-full"></div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
          {/* Content */}
          <motion.div
            initial={{opacity: 0, x: -50}}
            animate={{opacity: 1, x: 0}}
            transition={{duration: 0.8}}
            className="text-center lg:text-left"
          >
            <motion.h1
              initial={{opacity: 0, y: 20}}
              animate={{opacity: 1, y: 0}}
              transition={{duration: 0.8, delay: 0.2}}
              className="text-4xl md:text-5xl lg:text-6xl font-bold text-gray-900 mb-6 leading-tight"
            >
              {(content?.hero_title || 'Sustainable Weight Loss for LIFE').split(' ').map((word, index) => (
                <span key={index}>
                  {word === 'LIFE' ? (
                    <span className="text-nutrition-600 relative">
                      LIFE
                      <motion.div
                        initial={{width: 0}}
                        animate={{width: '100%'}}
                        transition={{duration: 0.8, delay: 1}}
                        className="absolute bottom-2 left-0 h-3 bg-nutrition-200 -z-10"
                      />
                    </span>
                  ) : (
                    word
                  )}{' '}
                </span>
              ))}
            </motion.h1>

            <motion.p
              initial={{opacity: 0, y: 20}}
              animate={{opacity: 1, y: 0}}
              transition={{duration: 0.8, delay: 0.4}}
              className="text-xl text-gray-600 mb-8 max-w-xl mx-auto lg:mx-0"
            >
              {content?.hero_subtitle || 'Simple, sustainable habits for people with real lives.'}
            </motion.p>

            <motion.div
              initial={{opacity: 0, y: 20}}
              animate={{opacity: 1, y: 0}}
              transition={{duration: 0.8, delay: 0.6}}
              className="flex flex-col sm:flex-row gap-4 justify-center lg:justify-start"
            >
              <Link
                to={content?.cta_primary_url || '/courses'}
                onClick={handlePrimaryCTA}
                className="bg-nutrition-600 hover:bg-nutrition-700 text-white px-8 py-4 rounded-lg font-semibold text-lg transition-all duration-300 transform hover:scale-105 flex items-center justify-center space-x-2 shadow-lg hover:shadow-xl"
              >
                <span>{content?.cta_primary_label || 'Explore Courses'}</span>
                <SafeIcon icon={FiArrowRight} className="w-5 h-5" />
              </Link>

              <a
                href={content?.cta_secondary_url || 'https://tidycal.com/empowernutrition/30-minute-consultation'}
                target="_blank"
                rel="noopener noreferrer"
                onClick={handleSecondaryCTA}
                className="border-2 border-nutrition-600 text-nutrition-600 hover:bg-nutrition-600 hover:text-white px-8 py-4 rounded-lg font-semibold text-lg transition-all duration-300 flex items-center justify-center space-x-2"
              >
                <SafeIcon icon={FiCalendar} className="w-5 h-5" />
                <span>{content?.cta_secondary_label || 'Book a Call'}</span>
              </a>
            </motion.div>

            {/* Trust Indicators */}
            <motion.div
              initial={{opacity: 0}}
              animate={{opacity: 1}}
              transition={{duration: 0.8, delay: 0.8}}
              className="mt-8 flex flex-col sm:flex-row items-center justify-center lg:justify-start space-y-4 sm:space-y-0 sm:space-x-8 text-sm text-gray-500"
            >
              <div className="flex items-center space-x-2">
                <SafeIcon icon={FiTrendingUp} className="w-4 h-4 text-nutrition-600" />
                <span>{content?.reviews_display_text || '50+ verified reviews on Google'}</span>
              </div>
              <div className="flex items-center space-x-2">
                <div className="w-2 h-2 bg-nutrition-500 rounded-full"></div>
                <span>Science-Based Methods</span>
              </div>
              <div className="flex items-center space-x-2">
                <div className="w-2 h-2 bg-nutrition-500 rounded-full"></div>
                <span>No Restrictive Diets</span>
              </div>
            </motion.div>
          </motion.div>

          {/* Hero Image/Visual */}
          <motion.div
            initial={{opacity: 0, x: 50}}
            animate={{opacity: 1, x: 0}}
            transition={{duration: 0.8, delay: 0.3}}
            className="relative"
          >
            <div className="relative z-10">
              <img
                src="https://images.unsplash.com/photo-1571019613454-1cb2f99b2d8b?w=600&h=500&fit=crop"
                alt="Healthy lifestyle transformation"
                className="w-full h-96 lg:h-[500px] object-cover rounded-2xl shadow-2xl"
              />

              {/* Floating Success Card */}
              <motion.div
                initial={{opacity: 0, y: 20}}
                animate={{opacity: 1, y: 0}}
                transition={{duration: 0.8, delay: 1}}
                className="absolute -bottom-6 -left-6 bg-white rounded-xl shadow-xl p-6 border border-gray-100"
              >
                <div className="flex items-center space-x-4">
                  <div className="w-12 h-12 bg-nutrition-100 rounded-full flex items-center justify-center">
                    <SafeIcon icon={FiTrendingUp} className="w-6 h-6 text-nutrition-600" />
                  </div>
                  <div>
                    <p className="text-2xl font-bold text-gray-900">92%</p>
                    <p className="text-sm text-gray-600">Keep weight off</p>
                    <p className="text-xs text-gray-500">after 1 year</p>
                  </div>
                </div>
              </motion.div>

              {/* Floating Habit Card */}
              <motion.div
                initial={{opacity: 0, scale: 0.8}}
                animate={{opacity: 1, scale: 1}}
                transition={{duration: 0.8, delay: 1.2}}
                className="absolute -top-6 -right-6 bg-nutrition-600 text-white rounded-xl shadow-xl p-4"
              >
                <div className="text-center">
                  <p className="text-lg font-bold">Simple</p>
                  <p className="text-sm opacity-90">Sustainable</p>
                  <p className="text-xs opacity-75">Habits</p>
                </div>
              </motion.div>
            </div>

            {/* Background Elements */}
            <div className="absolute top-4 right-4 w-32 h-32 bg-nutrition-200 rounded-full opacity-20 -z-10"></div>
            <div className="absolute bottom-4 left-4 w-24 h-24 bg-nutrition-300 rounded-full opacity-30 -z-10"></div>
          </motion.div>
        </div>
      </div>
    </section>
  )
}

export default HeroSection